import React, { useState } from 'react';

const AdminAuditSettings: React.FC = () => {
  const [values, setValues] = useState({
    logRetentionDays: 365,
    auditLogLevel: 'INFO',
    enableSensitiveDataMasking: true,
    enablePerformanceLogging: true,
    healthCheckIntervalMinutes: 5,
    enableErrorTracking: true,
    maxLogFileSize: 100,
    logRotationDays: 7,
    enableUserActivityLogging: true,
    enableSystemEventLogging: true,
    enableSecurityEventLogging: true,
    logCompressionEnabled: true
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const handleInputChange = (field: string, value: string | boolean | number) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveMessage('Audit settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '32px auto', 
      padding: '32px',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ 
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #f3f4f6'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            📊 Audit & Logging Settings
          </span>
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#6b7280', 
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Configure audit logging, system monitoring, and log retention policies
        </p>
      </div>

      {/* Log Retention & Management */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📁 Log Retention & Management
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Log Retention Period (days)
            </label>
            <input
              type="number"
              min={30}
              max={2555}
              value={values.logRetentionDays}
              onChange={e => handleInputChange('logRetentionDays', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '4px'
            }}>
              How long to keep audit logs (30-2555 days)
            </div>
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Audit Log Level
            </label>
            <select
              value={values.auditLogLevel}
              onChange={e => handleInputChange('auditLogLevel', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
                background: '#ffffff'
              }}
            >
              <option value="DEBUG">DEBUG</option>
              <option value="INFO">INFO</option>
              <option value="WARN">WARN</option>
              <option value="ERROR">ERROR</option>
            </select>
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Max Log File Size (MB)
            </label>
            <input
              type="number"
              min={10}
              max={1000}
              value={values.maxLogFileSize}
              onChange={e => handleInputChange('maxLogFileSize', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Log Rotation (days)
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={values.logRotationDays}
              onChange={e => handleInputChange('logRotationDays', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* System Monitoring */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '12px',
        border: '1px solid #fbbf24'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🔍 System Monitoring
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Health Check Interval (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={60}
              value={values.healthCheckIntervalMinutes}
              onChange={e => handleInputChange('healthCheckIntervalMinutes', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '4px'
            }}>
              How often to perform system health checks
            </div>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <input
              type="checkbox"
              checked={values.enablePerformanceLogging}
              onChange={e => handleInputChange('enablePerformanceLogging', e.target.checked)}
              style={{ 
                width: '20px',
                height: '20px',
                accentColor: '#f59e0b'
              }}
            />
            <div>
              <label style={{ 
                fontWeight: '600',
                color: '#374151',
                fontSize: '16px',
                margin: 0,
                cursor: 'pointer'
              }}>
                Enable Performance Logging
              </label>
              <div style={{ 
                fontSize: '13px', 
                color: '#6b7280',
                marginTop: '4px'
              }}>
                Log system performance metrics
              </div>
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <input
            type="checkbox"
            checked={values.enableErrorTracking}
            onChange={e => handleInputChange('enableErrorTracking', e.target.checked)}
            style={{ 
              width: '20px',
              height: '20px',
              accentColor: '#f59e0b'
            }}
          />
          <div>
            <label style={{ 
              fontWeight: '600',
              color: '#374151',
              fontSize: '16px',
              margin: 0,
              cursor: 'pointer'
            }}>
              Enable Error Tracking
            </label>
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Track and log application errors
            </div>
          </div>
        </div>
      </div>

      {/* Data Privacy & Security */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: '12px',
        border: '1px solid #d1fae5'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🔒 Data Privacy & Security
        </h3>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          marginBottom: '16px'
        }}>
          <input
            type="checkbox"
            checked={values.enableSensitiveDataMasking}
            onChange={e => handleInputChange('enableSensitiveDataMasking', e.target.checked)}
            style={{ 
              width: '20px',
              height: '20px',
              accentColor: '#10b981'
            }}
          />
          <div>
            <label style={{ 
              fontWeight: '600',
              color: '#374151',
              fontSize: '16px',
              margin: 0,
              cursor: 'pointer'
            }}>
              Enable Sensitive Data Masking
            </label>
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Mask sensitive information in audit logs
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          marginBottom: '16px'
        }}>
          <input
            type="checkbox"
            checked={values.logCompressionEnabled}
            onChange={e => handleInputChange('logCompressionEnabled', e.target.checked)}
            style={{ 
              width: '20px',
              height: '20px',
              accentColor: '#10b981'
            }}
          />
          <div>
            <label style={{ 
              fontWeight: '600',
              color: '#374151',
              fontSize: '16px',
              margin: 0,
              cursor: 'pointer'
            }}>
              Enable Log Compression
            </label>
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280',
              marginTop: '4px'
            }}>
              Compress log files to save storage space
            </div>
          </div>
        </div>
      </div>

      {/* Event Logging */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        borderRadius: '12px',
        border: '1px solid #fecaca'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📝 Event Logging
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px'
        }}>
          {[
            { key: 'enableUserActivityLogging', label: 'User Activity Logging' },
            { key: 'enableSystemEventLogging', label: 'System Event Logging' },
            { key: 'enableSecurityEventLogging', label: 'Security Event Logging' }
          ].map(item => (
            <div key={item.key} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <input
                type="checkbox"
                checked={values[item.key as keyof typeof values] as boolean}
                onChange={e => handleInputChange(item.key, e.target.checked)}
                style={{ 
                  width: '18px',
                  height: '18px',
                  accentColor: '#ef4444'
                }}
              />
              <label style={{ 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px',
                margin: 0,
                cursor: 'pointer'
              }}>
                {item.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '16px 32px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isSaving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save Audit Settings
            </>
          )}
        </button>
        
        {saveMessage && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: saveMessage.includes('successfully') ? '#065f46' : '#991b1b',
            background: saveMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${saveMessage.includes('successfully') ? '#a7f3d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {saveMessage.includes('successfully') ? '✅' : '❌'} {saveMessage}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminAuditSettings;
